import { useQuery } from '@tanstack/react-query';

import useApi from './use-api';

const POLL_INTERVAL_MS = 1000 * 10;

export interface ILicensedCapacity {
  // Normalized 2-letter code for the state the counts describe, or null when
  // the patient's state could not be resolved to a real US state.
  state: string | null;
  licensedOnline: number | null;
  licensedAvailable: number | null;
  // ISO code of the patient's language when it narrows routing, else null.
  // English is always null: every nurse speaks it, so a subset would merely
  // restate the counts above.
  language: string | null;
  // The SUBSET of licensedOnline/licensedAvailable who also speak `language`.
  // Nested, never additive — languageAvailable <= licensedAvailable always.
  // Null counts alongside a non-null `language` mean the subset was
  // undeterminable, which must never be rendered as zero.
  languageOnline: number | null;
  languageAvailable: number | null;
}

interface IProps {
  patientGuid?: string | null;
  canLoad?: boolean;
}

export const useLicensedCapacity = (props: IProps) => {
  const { patientGuid, canLoad } = props;

  const { loadData } = useApi({
    showError: false,
  });

  const queryKey = ['getLicensedCapacity', patientGuid];

  const { data, isInitialLoading, dataUpdatedAt } = useQuery({
    queryKey,
    queryFn: async () => {
      // This endpoint nests its payload under `data`, unlike most core
      // endpoints which return it at the top level.
      const res = await loadData<{ data?: ILicensedCapacity }>(
        'core/integrations/connect-center/licensed-capacity',
        { patientGuid },
      );

      if (res?.data) {
        return res.data;
      }

      return undefined;
    },
    enabled: Boolean(canLoad && patientGuid),
    refetchInterval: POLL_INTERVAL_MS,
    // The backend caches per state for ~10s; a shorter staleTime would only
    // add requests that return the same cached value.
    staleTime: POLL_INTERVAL_MS,
  });

  return {
    data,
    // `isInitialLoading`, not `isLoading`: a disabled query (no patientGuid)
    // reports `isLoading: true` indefinitely, which would pin the readout to
    // its skeleton for a caller we can never resolve. This is true only while
    // a first fetch is genuinely in flight.
    isLoading: isInitialLoading,
    // The readout states its own age rather than implying the value is live —
    // the client polls every 10s and the backend caches per state for ~10s, so
    // a displayed number can be up to ~20s old.
    dataUpdatedAt,
  };
};
